import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { apiJson, ApiError } from "../../lib/api";
import FeedbackWidget from "./FeedbackWidget";

type UiField = {
  key: string;
  label?: string;
  widget?: "text" | "textarea" | string;
  placeholder?: string;
};

type IngressApp = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  flow_id: string;
  version_id: string;
  rate_limit_rps?: number | null;
  input_json_schema?: any;
  ui_schema?: { title?: string; fields?: UiField[] };
  output_rules?: any;
};

type RequestStatus = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  run_id?: string | null;
  error?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
};

type IngressOutput = {
  output_json: any;
  redactions?: any;
  policy_summary?: any;
  run_id?: string | null;
  created_at?: string;

  // Server-side contract fields (preferred)
  final_output?: any;
  final_output_kind?: "text" | "json" | "unknown" | string;
  final_output_path?: string;
};

/**
 * Tenant resolution:
 * - Prefer URL query param ?tenant_id=<uuid> (shareable link)
 * - Else fallback to localStorage (persisted after landing page selection)
 */
const LS_TENANT_KEY = "ingress_tenant_uuid_v1";

function isUuidLike(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s || ""
  );
}

function getTenantUuidFromSearch(search: string): string | null {
  try {
    const sp = new URLSearchParams(search || "");
    const tid = String(sp.get("tenant_id") || "").trim();
    return tid && isUuidLike(tid) ? tid : null;
  } catch {
    return null;
  }
}

// ---------------------
// Chat / history / memory (optional)
// ---------------------
/**
 * These endpoints must exist to enable chat mode:
 *   POST /v1/ingress/apps/{slug}/threads
 *   GET  /v1/ingress/threads/{thread_id}/messages
 *
 * If your backend doesn’t have them yet, the page remains a single-run experience.
 */
type ThreadCreateResp = { id?: string; thread_id?: string };
type ThreadMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  content_json?: any;
  created_at: string;
  request_id?: string | null;
};

function isObject(v: any): v is Record<string, any> {
  return v && typeof v === "object" && !Array.isArray(v);
}

function pretty(v: any) {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function extractPolicyEvents(outputJson: any) {
  const log = isObject(outputJson) ? (outputJson as any).log : null;
  const items: any[] = Array.isArray(log) ? log : [];
  return items
    .filter((e) => isObject(e) && isObject((e as any).extra) && (e as any).extra.event === "policy_engine")
    .map((e) => ({
      ts: (e as any).ts,
      node_id: (e as any).node_id,
      level: (e as any).level,
      message: (e as any).message,
      stage: (e as any).extra.stage,
      action: (e as any).extra.action,
      reason: (e as any).extra.reason,
      findings: (e as any).extra.findings || [],
    }));
}

function requiredKeysFromSchema(schema: any): string[] {
  if (!schema || typeof schema !== "object") return [];
  const req = (schema as any).required;
  return Array.isArray(req) ? req.map(String) : [];
}

/**
 * 1) Prefer ui_schema.fields
 * 2) Fallback to input_json_schema.properties
 * 3) Fallback to a default text field
 */
function fieldListFromApp(app: IngressApp | null): UiField[] {
  const uiFields = app?.ui_schema?.fields;
  if (Array.isArray(uiFields) && uiFields.length) {
    return uiFields
      .map((f) => ({
        key: String((f as any).key || "").trim(),
        label: (f as any).label,
        widget: ((f as any).widget as any) || "text",
        placeholder: (f as any).placeholder,
      }))
      .filter((f) => !!f.key);
  }

  const props = app?.input_json_schema?.properties;
  if (props && typeof props === "object") {
    return Object.keys(props).map((k) => ({ key: k, label: k, widget: "text" }));
  }

  return [{ key: "text", label: "Input", widget: "textarea" }];
}

// --- schema-based coercion helpers (fix integer/number/boolean fields) ---
function coerceScalarBySchema(value: any, propSchema: any) {
  if (!propSchema || typeof propSchema !== "object") return value;

  const t = String((propSchema as any).type || "").toLowerCase();
  if (!t) return value;

  // Don't coerce null/undefined
  if (value === null || value === undefined) return value;

  // Integer
  if (t === "integer") {
    if (typeof value === "number" && Number.isInteger(value)) return value;
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return value;
      const n = Number(s);
      if (Number.isFinite(n)) return Math.trunc(n);
    }
    return value;
  }

  // Number
  if (t === "number") {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return value;
      const n = Number(s);
      if (Number.isFinite(n)) return n;
    }
    return value;
  }

  // Boolean
  if (t === "boolean") {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      if (s === "true") return true;
      if (s === "false") return false;
    }
    return value;
  }

  return value;
}

function coerceInputsBySchema(rawInputs: Record<string, any>, inputSchema: any): Record<string, any> {
  const props = inputSchema?.properties && typeof inputSchema.properties === "object" ? inputSchema.properties : {};
  const out: Record<string, any> = { ...(rawInputs || {}) };

  for (const k of Object.keys(out)) {
    out[k] = coerceScalarBySchema(out[k], (props as any)[k]);
  }
  return out;
}

function looksLikeJsonString(s: string) {
  const t = (s || "").trim();
  return t.startsWith("{") || t.startsWith("[");
}

/**
 * Fanout “envelope” unwrapping:
 * - First try JSON.parse and extract common shapes
 * - If JSON.parse fails (e.g. contains [REDACTED]), regex-extract first "text":"..."
 */
function unwrapFanoutTextBestEffort(maybeEnvelope: string): { text: string | null; path: string | null } {
  const s = String(maybeEnvelope ?? "");
  const t = s.trim();
  if (!t) return { text: null, path: null };

  // 1) JSON parse path (works when string is valid JSON)
  if (looksLikeJsonString(t)) {
    try {
      const obj = JSON.parse(t);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        const out = (obj as any).output;
        if (out && typeof out === "object" && !Array.isArray(out)) {
          if (typeof out.text === "string") return { text: out.text, path: "parsed.output.text" };
          const out2 = out.output;
          if (out2 && typeof out2 === "object" && !Array.isArray(out2) && typeof out2.text === "string") {
            return { text: out2.text, path: "parsed.output.output.text" };
          }
          const win = out.winner_output;
          if (win && typeof win === "object" && !Array.isArray(win) && typeof win.text === "string") {
            return { text: win.text, path: "parsed.output.winner_output.text" };
          }
        }

        const win2 = (obj as any).winner_output;
        if (win2 && typeof win2 === "object" && !Array.isArray(win2) && typeof win2.text === "string") {
          return { text: win2.text, path: "parsed.winner_output.text" };
        }

        if (typeof (obj as any).text === "string") return { text: (obj as any).text, path: "parsed.text" };
      }
    } catch {
      // fall through to regex
    }
  }

  // 2) Regex fallback for invalid JSON strings (common with [REDACTED])
  {
    const m = t.match(/"text"\s*:\s*"([^"]+)"/);
    if (m && m[1]) return { text: m[1], path: "regex.text" };
  }
  {
    const m = t.match(/\\"text\\"\s*:\s*\\"([^\\"]+)\\"/);
    if (m && m[1]) return { text: m[1], path: "regex.escaped_text" };
  }

  return { text: null, path: null };
}

function extractFinalAnswerLegacy(app: IngressApp | null, out: any): string | null {
  if (!app || !out) return null;

  const outputs = out?.output_json?.outputs;
  if (!isObject(outputs)) return null;

  const keys = Object.keys(outputs);
  if (keys.length === 0) return null;

  const preferred =
    keys.length === 1
      ? keys[0]
      : (["final", "result", "output", "make_text", "agg", "llm1"].find((k) => keys.includes(k)) || keys[0]);

  const primary = (outputs as any)[preferred];

  if (typeof primary === "string") return primary;

  if (isObject(primary)) {
    for (const k of ["text", "answer", "result", "message", "content"]) {
      if (typeof (primary as any)[k] === "string") return String((primary as any)[k]);
    }

    if (typeof (primary as any).text === "string") return String((primary as any).text);
    return JSON.stringify(primary);
  }

  if (typeof (outputs as any).text === "string") return String((outputs as any).text);
  return null;
}

function extractFinalAnswer(app: IngressApp | null, out: IngressOutput | null): { answer: string | null; meta?: string } {
  if (!app || !out) return { answer: null };

  if (out.final_output !== undefined) {
    const kind = String(out.final_output_kind || "unknown");
    const path = String(out.final_output_path || "");

    if (typeof out.final_output === "string") {
      const s = out.final_output;
      const unwrapped = unwrapFanoutTextBestEffort(s);
      if (unwrapped.text) {
        const meta = [kind, path, unwrapped.path].filter(Boolean).join(" · ");
        return { answer: unwrapped.text, meta };
      }
      const meta = [kind, path].filter(Boolean).join(" · ");
      return { answer: s, meta };
    }

    if (typeof out.final_output === "object" && out.final_output !== null) {
      const meta = [kind, path].filter(Boolean).join(" · ");
      return { answer: pretty(out.final_output), meta };
    }

    const meta = [kind, path].filter(Boolean).join(" · ");
    return { answer: String(out.final_output ?? ""), meta };
  }

  const legacy = extractFinalAnswerLegacy(app, out as any);
  if (legacy && typeof legacy === "string") {
    const unwrapped = unwrapFanoutTextBestEffort(legacy);
    if (unwrapped.text) return { answer: unwrapped.text, meta: unwrapped.path || undefined };
  }
  return { answer: legacy };
}

function isArrayOfObjects(v: any): v is Array<Record<string, any>> {
  return Array.isArray(v) && v.every((x) => x && typeof x === "object" && !Array.isArray(x));
}

function FinalOutputView(props: { out: IngressOutput; finalAnswerText: string | null }) {
  const { out, finalAnswerText } = props;
  const kind = String(out.final_output_kind || "");
  const fo = out.final_output;

  if (kind === "json" && fo !== undefined && fo !== null) {
    if (isArrayOfObjects(fo)) {
      const cols = Array.from(
        fo.reduce((set, row) => {
          Object.keys(row || {}).forEach((k) => set.add(k));
          return set;
        }, new Set<string>())
      );

      return (
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-2">
            Showing structured output ({fo.length} item{fo.length === 1 ? "" : "s"}).
          </div>

          <div className="border rounded overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr className="border-b">
                  {cols.map((c) => (
                    <th key={c} className="text-left py-2 px-3 whitespace-nowrap">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fo.map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    {cols.map((c) => {
                      const v = (row as any)[c];
                      const s =
                        v == null
                          ? "—"
                          : typeof v === "string"
                          ? v
                          : typeof v === "number" || typeof v === "boolean"
                          ? String(v)
                          : pretty(v);
                      return (
                        <td key={c} className="py-2 px-3 align-top">
                          <div className="whitespace-pre-wrap">{s}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (isObject(fo)) {
      const entries = Object.entries(fo);
      return (
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-2">Showing structured output.</div>
          <div className="border rounded overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Key</th>
                  <th className="text-left py-2 px-3">Value</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([k, v]) => (
                  <tr key={k} className="border-b last:border-b-0">
                    <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{k}</td>
                    <td className="py-2 px-3">
                      <div className="whitespace-pre-wrap">{v == null ? "—" : typeof v === "string" ? v : pretty(v)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return <div className="mt-2 border rounded bg-gray-50 p-3 text-sm whitespace-pre-wrap">{finalAnswerText ?? pretty(fo)}</div>;
  }

  return <div className="mt-2 border rounded bg-gray-50 p-3 text-sm whitespace-pre-wrap">{finalAnswerText ?? "(No primary output detected)"}</div>;
}

function lsKeyForThread(slug: string) {
  return `ingress_thread:${slug}`;
}

function genUUIDv4(): string {
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function renderMessageBubble(m: ThreadMessage) {
  const isUser = m.role === "user";
  return (
    <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm whitespace-pre-wrap ${isUser ? "bg-blue-50 border-blue-200" : "bg-white"}`}>
        {m.content}
        <div className="mt-1 text-[11px] text-gray-400">
          {new Date(m.created_at).toLocaleString()}
          {m.request_id ? (
            <>
              {" "}
              · <span className="font-mono">{String(m.request_id).slice(0, 8)}…</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function IngressRunnerPage() {
  const { slug } = useParams();
  const loc = useLocation();

  // Tenant UUID: URL param wins (shareable), else localStorage fallback
  const tenantUuid = useMemo(() => {
    const fromUrl = getTenantUuidFromSearch(loc.search);
    if (fromUrl) {
      try { localStorage.setItem(LS_TENANT_KEY, fromUrl); } catch {}
      return fromUrl;
    }
    const fromLs = String(localStorage.getItem(LS_TENANT_KEY) || "").trim();
    return fromLs && isUuidLike(fromLs) ? fromLs : "";
  }, [loc.search]);

  // Preserve tenant_id in navigation links
  const tenantQs = useMemo(() => (tenantUuid ? `?tenant_id=${encodeURIComponent(tenantUuid)}` : ""), [tenantUuid]);

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState<IngressApp | null>(null);

  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [req, setReq] = useState<RequestStatus | null>(null);
  const [out, setOut] = useState<IngressOutput | null>(null);

  const pollTimer = useRef<number | null>(null);

  const fields = useMemo<UiField[]>(() => fieldListFromApp(app), [app]);
  const requiredKeys = useMemo(() => requiredKeysFromSchema(app?.input_json_schema), [app]);

  // Chat/history state
  const [chatSupported, setChatSupported] = useState(false);
  const [mode, setMode] = useState<"single" | "chat">("single");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [chatText, setChatText] = useState("");
  const [useMemory, setUseMemory] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  async function detectChatSupport(slug2: string) {
    try {
      const testId = genUUIDv4();
      await apiJson<ThreadCreateResp>(
        `/v1/ingress/apps/${encodeURIComponent(slug2)}/threads?tenant_id=${encodeURIComponent(tenantUuid)}`,
        {
          method: "POST",
          body: JSON.stringify({ client_probe_id: testId }),
        }
      );
      return true;
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e);
      if (msg.includes("404")) return false;
      return false;
    }
  }

  async function createThread(slug2: string): Promise<string> {
    const resp = await apiJson<ThreadCreateResp>(
      `/v1/ingress/apps/${encodeURIComponent(slug2)}/threads?tenant_id=${encodeURIComponent(tenantUuid)}`,
      { method: "POST", body: JSON.stringify({}) }
    );

    const tid = (resp as any)?.thread_id || (resp as any)?.id;
    if (!tid) throw new Error("Thread create did not return thread id (expected id or thread_id).");
    return String(tid);
  }

  async function loadMessages(th: string) {
    setLoadingMsgs(true);
    try {
      const resp = await apiJson<any>(
        `/v1/ingress/threads/${encodeURIComponent(th)}?tenant_id=${encodeURIComponent(tenantUuid)}`
      );
      const msgs = resp?.messages || resp?.thread?.messages || [];
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function run() {
      setLoading(true);
      setSubmitError(null);
      setFieldErrors({});
      setReq(null);
      setOut(null);

      try {
        if (!slug) throw new Error("Missing app slug.");
        if (!tenantUuid) throw new Error("Missing tenant_id. Open from /app/ingress?tenant_id=<uuid>.");

        const data = await apiJson<IngressApp>(
          `/v1/ingress/apps/${encodeURIComponent(String(slug))}?tenant_id=${encodeURIComponent(tenantUuid)}`
        );

        if (!mounted) return;
        setApp(data);
        setInputs({});

        // detect chat support + restore thread
        const supported = await detectChatSupport(String(slug));
        if (!mounted) return;
        setChatSupported(supported);

        if (supported) {
          const saved = localStorage.getItem(lsKeyForThread(String(slug)));
          if (saved && saved.trim()) {
            setThreadId(saved);
          }
        }
      } catch (e: any) {
        if (!mounted) return;
        setApp(null);
        setSubmitError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, [slug, tenantUuid]);

  useEffect(() => {
    if (mode !== "chat") return;
    if (!threadId) return;
    loadMessages(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, threadId]);

  function validateClientRequired(): boolean {
    const errs: Record<string, string> = {};
    for (const k of requiredKeys) {
      const v = inputs[k];
      const empty = v == null || (typeof v === "string" && v.trim() === "");
      if (empty) errs[k] = "Required";
    }
    setFieldErrors(errs);

    if (Object.keys(errs).length > 0) {
      setSubmitError("Please fill all required fields.");
      return false;
    }
    return true;
  }

  async function submitSingle() {
    if (!slug) return;

    setSubmitError(null);
    setFieldErrors({});
    setReq(null);
    setOut(null);

    if (!tenantUuid) {
      setSubmitError("Missing tenant_id. Open from /app/ingress?tenant_id=<uuid>.");
      return;
    }

    if (!validateClientRequired()) return;

    try {
      const coercedInputs = coerceInputsBySchema(inputs, app?.input_json_schema);

      const resp = await apiJson<{ request_id: string; status: string }>(
        `/v1/ingress/apps/${encodeURIComponent(String(slug))}/requests?tenant_id=${encodeURIComponent(tenantUuid)}`,
        { method: "POST", body: JSON.stringify({ inputs: coercedInputs }) }
      );

      const requestId = resp.request_id;
      if (!requestId) throw new Error("No request_id returned from server.");

      setReq({
        id: requestId,
        status: (resp.status as any) || "queued",
        created_at: new Date().toISOString(),
      } as any);

      if (pollTimer.current) window.clearInterval(pollTimer.current);
      pollTimer.current = window.setInterval(async () => {
        try {
          const st = await apiJson<RequestStatus>(
            `/v1/ingress/requests/${requestId}?tenant_id=${encodeURIComponent(tenantUuid)}`
          );
          setReq(st);

          if (st.status === "succeeded" || st.status === "failed") {
            if (pollTimer.current) window.clearInterval(pollTimer.current);
            pollTimer.current = null;

            if (st.status === "succeeded") {
              const o = await apiJson<IngressOutput>(
                `/v1/ingress/requests/${requestId}/output?tenant_id=${encodeURIComponent(tenantUuid)}`
              );
              setOut(o);
            } else {
              setSubmitError(st.error || "Request failed.");
            }
          }
        } catch {
          // ignore transient polling errors
        }
      }, 1000);
    } catch (e: any) {
      setSubmitError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function submitChat() {
    if (!slug) return;
    if (!chatSupported) {
      setSubmitError("Chat mode is not enabled on the backend yet.");
      return;
    }

    if (!tenantUuid) {
      setSubmitError("Missing tenant_id. Open from /app/ingress?tenant_id=<uuid>.");
      return;
    }

    const msg = chatText.trim();
    if (!msg) {
      setSubmitError("Type a message first.");
      return;
    }

    setSubmitError(null);
    setReq(null);
    setOut(null);

    let th = threadId;
    try {
      if (!th) {
        th = await createThread(String(slug));
        setThreadId(th);
        localStorage.setItem(lsKeyForThread(String(slug)), th);
      }

      const inputsObj: Record<string, any> = { ...inputs };
      if (fields.length === 1 && fields[0].key && typeof inputsObj[fields[0].key] !== "string") {
        inputsObj[fields[0].key] = msg;
      }

      const resp = await apiJson<{ request_id: string; status: string }>(
        `/v1/ingress/apps/${encodeURIComponent(String(slug))}/requests?tenant_id=${encodeURIComponent(tenantUuid)}`,
        {
          method: "POST",
          body: JSON.stringify({
            inputs: inputsObj,
            thread_id: th,
            message: msg,
            ignore_memory: !useMemory,
          }),
        }
      );

      const requestId = resp.request_id;
      if (!requestId) throw new Error("No request_id returned from server.");

      setChatText("");

      setReq({
        id: requestId,
        status: (resp.status as any) || "queued",
        created_at: new Date().toISOString(),
      } as any);

      if (pollTimer.current) window.clearInterval(pollTimer.current);
      pollTimer.current = window.setInterval(async () => {
        try {
          const st = await apiJson<RequestStatus>(
            `/v1/ingress/requests/${requestId}?tenant_id=${encodeURIComponent(tenantUuid)}`
          );
          setReq(st);

          if (st.status === "succeeded" || st.status === "failed") {
            if (pollTimer.current) window.clearInterval(pollTimer.current);
            pollTimer.current = null;

            if (st.status === "succeeded") {
              const o = await apiJson<IngressOutput>(
                `/v1/ingress/requests/${requestId}/output?tenant_id=${encodeURIComponent(tenantUuid)}`
              );
              setOut(o);
              if (th) await loadMessages(th);
            } else {
              setSubmitError(st.error || "Request failed.");
              if (th) await loadMessages(th);
            }
          }
        } catch {
          // ignore transient polling errors
        }
      }, 1000);
    } catch (e: any) {
      setSubmitError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function resetConversation() {
    if (!slug) return;
    setThreadId(null);
    setMessages([]);
    localStorage.removeItem(lsKeyForThread(String(slug)));
  }

  useEffect(() => {
    return () => {
      if (pollTimer.current) window.clearInterval(pollTimer.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-lg font-semibold">Loading…</div>
      </div>
    );
  }

  if (!tenantUuid) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="text-xl font-semibold">Missing tenant_id</div>
        <div className="mt-2 text-sm text-gray-600">
          Open the Ingress landing page with a tenant, then launch an app from there:
          <div className="mt-2">
            <code className="bg-gray-100 rounded px-2 py-1">/app/ingress?tenant_id=&lt;tenant-uuid&gt;</code>
          </div>
        </div>
        <div className="mt-6">
          <Link className="text-blue-700 hover:underline text-sm" to="/app/ingress">
            Back to Ingress
          </Link>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="p-6">
        <div className="text-xl font-semibold">Ingress App Not Found</div>
        <div className="mt-2 text-sm text-gray-600">Slug: {String(slug)}</div>
        {submitError && <pre className="mt-4 text-sm text-red-600 whitespace-pre-wrap">{submitError}</pre>}
        <div className="mt-6">
          <Link className="text-blue-700 hover:underline text-sm" to={`/app/ingress${tenantQs}`}>
            Back to Ingress
          </Link>
        </div>
      </div>
    );
  }

  const policyEvents = extractPolicyEvents(out?.output_json);
  const { answer: finalAnswer, meta: finalMeta } = extractFinalAnswer(app, out);
  const adminRequestUrl = req?.id ? `/app/admin/ingress/requests/${req.id}` : null;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{app.name}</h1>
          {app.description && <p className="mt-1 text-gray-600">{app.description}</p>}
          <div className="mt-2 text-xs text-gray-500">
            <span className="mr-3">
              tenant: <code>{tenantUuid}</code>
            </span>
            <span className="mr-3">
              slug: <code>{app.slug}</code>
            </span>
            <span className="mr-3">
              flow: <code>{app.flow_id}</code>
            </span>
            <span>
              version: <code>{app.version_id}</code>
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <Link className="text-sm px-3 py-2 rounded border hover:bg-gray-50" to={`/app/ingress${tenantQs}`}>
            Ingress Home
          </Link>

          <Link
            className={`text-sm px-3 py-2 rounded border hover:bg-gray-50 ${
              !chatSupported ? "opacity-50 pointer-events-none" : ""
            }`}
            to={`/app/ingress/${encodeURIComponent(app.slug)}/chat${tenantQs}`}
            target="_blank"
            rel="noreferrer"
            title={!chatSupported ? "Chat endpoints not enabled on backend" : "Open Chat Runner in a new tab"}
          >
            Open Chat Runner
          </Link>

          <a className="text-sm px-3 py-2 rounded bg-gray-900 text-white hover:bg-black" href="/app/admin">
            Admin
          </a>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <button
            className={`px-3 py-2 rounded border ${mode === "single" ? "bg-gray-900 text-white" : "hover:bg-gray-50"}`}
            onClick={() => setMode("single")}
            type="button"
          >
            Single Run
          </button>
          <button
            className={`px-3 py-2 rounded border ${
              mode === "chat" ? "bg-gray-900 text-white" : "hover:bg-gray-50"
            } ${!chatSupported ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => {
              if (!chatSupported) return;
              setMode("chat");
            }}
            type="button"
            title={!chatSupported ? "Chat endpoints not enabled on backend" : "Chat with history"}
          >
            Chat
          </button>

          {mode === "chat" && (
            <div className="ml-2 text-xs text-gray-500">
              thread: <code>{threadId ? threadId.slice(0, 8) + "…" : "none"}</code>
            </div>
          )}
        </div>

        {mode === "chat" && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={useMemory} onChange={(e) => setUseMemory(e.target.checked)} />
              Use memory
            </label>
            <button className="px-3 py-2 rounded border hover:bg-gray-50 text-sm" onClick={resetConversation} type="button">
              Reset thread
            </button>
          </div>
        )}
      </div>

      {submitError && (
        <div className="mt-4 p-3 rounded border border-red-300 bg-red-50 text-sm text-red-700 whitespace-pre-wrap">{submitError}</div>
      )}

      {mode === "chat" ? (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chat */}
          <div className="rounded-lg border bg-white p-4">
            <div className="font-semibold">Chat</div>
            <div className="mt-1 text-xs text-gray-500">
              Sends messages through the same ingress endpoint, and stores history if backend thread APIs are enabled.
            </div>

            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-gray-700">Optional structured inputs</summary>
              <div className="mt-3 space-y-3">
                {fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium mb-1">{f.label || f.key}</label>
                    {f.widget === "text" ? (
                      <input
                        className="w-full border rounded px-3 py-2"
                        placeholder={f.placeholder || ""}
                        value={inputs[f.key] ?? ""}
                        onChange={(e) => setInputs((p) => ({ ...p, [f.key]: e.target.value }))}
                      />
                    ) : (
                      <textarea
                        className="w-full border rounded px-3 py-2 min-h-[90px]"
                        placeholder={f.placeholder || ""}
                        value={inputs[f.key] ?? ""}
                        onChange={(e) => setInputs((p) => ({ ...p, [f.key]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </details>

            <div className="mt-4 border rounded p-3 bg-gray-50">
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[110px]"
                placeholder="Type a message…"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
              />
              <div className="mt-2 flex gap-2">
                <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={submitChat} type="button">
                  Send
                </button>
                <button className="px-4 py-2 rounded border hover:bg-white" onClick={() => setChatText("")} type="button">
                  Clear
                </button>
              </div>
            </div>

            {/* Status */}
            <div className="mt-4 rounded border p-3">
              <div className="font-semibold text-sm">Latest request</div>
              {!req ? (
                <div className="mt-2 text-sm text-gray-600">No request yet.</div>
              ) : (
                <div className="mt-2 text-sm space-y-1">
                  <div>
                    <span className="text-gray-600">request_id:</span> <code>{req.id}</code>
                  </div>
                  <div>
                    <span className="text-gray-600">status:</span> <span className="font-medium">{req.status}</span>
                  </div>
                  {req.run_id && (
                    <div>
                      <span className="text-gray-600">run_id:</span> <code>{req.run_id}</code>
                    </div>
                  )}
                  {adminRequestUrl && (
                    <div className="pt-2">
                      <Link className="text-blue-700 hover:underline text-sm" to={adminRequestUrl} target="_blank">
                        Open in Admin →
                      </Link>
                    </div>
                  )}
                  {req.error && <div className="pt-2 text-red-600 whitespace-pre-wrap">{req.error}</div>}
                </div>
              )}
            </div>
          </div>

          {/* History + Output */}
          <div className="space-y-6">
            <div className="rounded-lg border bg-white p-4">
              <div className="font-semibold">History</div>
              {loadingMsgs ? (
                <div className="mt-3 text-sm text-gray-600">Loading…</div>
              ) : messages.length === 0 ? (
                <div className="mt-3 text-sm text-gray-600">No messages yet.</div>
              ) : (
                <div className="mt-3 space-y-2 max-h-[520px] overflow-auto">
                  {messages.map((m) => renderMessageBubble(m))}
                </div>
              )}
            </div>

            <div className="rounded-lg border bg-white p-4">
              <div className="font-semibold">Latest Output</div>
              {!out ? (
                <div className="mt-3 text-sm text-gray-600">Output not available yet.</div>
              ) : (
                <div className="mt-3 space-y-4">
                  <div>
                    <div className="text-sm font-medium">Final Output</div>
                    <FinalOutputView out={out} finalAnswerText={finalAnswer} />

                    {(out.final_output_kind || out.final_output_path || finalMeta) && (
                      <div className="mt-2 text-xs text-gray-500">
                        {out.final_output_kind ? (
                          <>
                            kind: <code>{String(out.final_output_kind)}</code> ·{" "}
                          </>
                        ) : null}
                        {out.final_output_path ? (
                          <>
                            path: <code>{String(out.final_output_path)}</code>
                          </>
                        ) : finalMeta ? (
                          <>
                            path: <code>{finalMeta}</code>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <details>
                    <summary className="cursor-pointer text-sm text-gray-700">Raw output (JSON)</summary>
                    <pre className="mt-2 text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[420px]">{pretty(out)}</pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Run */}
            <div className="rounded-lg border bg-white p-4">
              <div className="font-semibold">Run</div>
              <div className="mt-1 text-xs text-gray-500">
                {requiredKeys.length > 0 ? (
                  <>
                    Required:{" "}
                    {requiredKeys.map((k) => (
                      <code key={k} className="mx-1">
                        {k}
                      </code>
                    ))}
                  </>
                ) : (
                  <>No required fields declared.</>
                )}
              </div>

              <div className="mt-3 space-y-3">
                {fields.map((f) => {
                  const isReq = requiredKeys.includes(f.key);
                  const fe = fieldErrors[f.key];

                  return (
                    <div key={f.key}>
                      <label className="block text-sm font-medium mb-1">
                        {f.label || f.key} {isReq ? <span className="text-red-600">*</span> : null}
                      </label>

                      {f.widget === "text" ? (
                        <input
                          className={`w-full border rounded px-3 py-2 ${fe ? "border-red-400" : ""}`}
                          placeholder={f.placeholder || ""}
                          value={inputs[f.key] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setInputs((p) => ({ ...p, [f.key]: v }));
                            setFieldErrors((p) => {
                              const n = { ...p };
                              delete n[f.key];
                              return n;
                            });
                          }}
                        />
                      ) : (
                        <textarea
                          className={`w-full border rounded px-3 py-2 min-h-[120px] ${fe ? "border-red-400" : ""}`}
                          placeholder={f.placeholder || ""}
                          value={inputs[f.key] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setInputs((p) => ({ ...p, [f.key]: v }));
                            setFieldErrors((p) => {
                              const n = { ...p };
                              delete n[f.key];
                              return n;
                            });
                          }}
                        />
                      )}

                      {fe && <div className="mt-1 text-xs text-red-600">{fe}</div>}
                    </div>
                  );
                })}

                <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={submitSingle} type="button">
                  Submit
                </button>
              </div>
            </div>

            {/* Status */}
            <div className="rounded-lg border bg-white p-4">
              <div className="font-semibold">Status</div>
              {!req ? (
                <div className="mt-3 text-sm text-gray-600">No request yet.</div>
              ) : (
                <div className="mt-3 text-sm space-y-1">
                  <div>
                    <span className="text-gray-600">request_id:</span> <code>{req.id}</code>
                  </div>
                  <div>
                    <span className="text-gray-600">status:</span> <span className="font-medium">{req.status}</span>
                  </div>
                  {req.run_id && (
                    <div>
                      <span className="text-gray-600">run_id:</span> <code>{req.run_id}</code>
                    </div>
                  )}
                  {adminRequestUrl && (
                    <div className="pt-2">
                      <Link className="text-blue-700 hover:underline text-sm" to={adminRequestUrl} target="_blank">
                        Open in Admin →
                      </Link>
                    </div>
                  )}
                  {req.error && <div className="pt-2 text-red-600 whitespace-pre-wrap">{req.error}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Output */}
          <div className="mt-6 rounded-lg border bg-white p-4">
            <div className="font-semibold">Output</div>

            {!out ? (
              <div className="mt-3 text-sm text-gray-600">Output not available yet.</div>
            ) : (
              <div className="mt-3 space-y-4">
                <div>
                  <div className="text-sm font-medium">Final Output</div>

                  <FinalOutputView out={out} finalAnswerText={finalAnswer} />

                  {(out.final_output_kind || out.final_output_path || finalMeta) && (
                    <div className="mt-2 text-xs text-gray-500">
                      {out.final_output_kind ? (
                        <>
                          kind: <code>{String(out.final_output_kind)}</code> ·{" "}
                        </>
                      ) : null}
                      {out.final_output_path ? (
                        <>
                          path: <code>{String(out.final_output_path)}</code>
                        </>
                      ) : finalMeta ? (
                        <>
                          path: <code>{finalMeta}</code>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>

                <details>
                  <summary className="cursor-pointer text-sm text-gray-700">Raw output (JSON)</summary>
                  <pre className="mt-2 text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[520px]">{pretty(out)}</pre>
                </details>

                <div>
                  <div className="text-sm font-medium">Policy / Guardrails</div>
                  {policyEvents.length === 0 ? (
                    <div className="mt-2 text-sm text-gray-600">No policy events recorded.</div>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {policyEvents.map((e, idx) => (
                        <div key={idx} className="border rounded p-3">
                          <div className="text-xs text-gray-500">
                            {e.ts} · node <code>{e.node_id}</code> · {e.stage} · action <b>{e.action}</b>
                          </div>
                          {e.reason && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-600">reason:</span> <code>{String(e.reason)}</code>
                            </div>
                          )}
                          {Array.isArray(e.findings) && e.findings.length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-xs text-gray-700">Findings ({e.findings.length})</summary>
                              <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto">{pretty(e.findings)}</pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium">Raw run log</div>
                  <pre className="mt-2 text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[360px]">{pretty(out.output_json?.log || [])}</pre>
                </div>
              </div>
            )}

            {req?.id ? <FeedbackWidget ingressRequestId={req.id} appSlug={app.slug} /> : null}
          </div>
        </>
      )}
    </div>
  );
}